import type { ImagePickerAsset, ImagePickerSuccessResult } from 'expo-image-picker';
import { pipe } from './fp';

export interface File {
  name: string;
  type: string;
  uri: string;
}

export function isImageAsset(x: unknown): x is ImagePickerAsset | ImagePickerSuccessResult {
  if (!!x && typeof x === 'object') {
    const imageAsset =
      'assets' in x && (x as ImagePickerSuccessResult).assets.length > 0
        ? (x as ImagePickerSuccessResult).assets[0]!
        : x;
    return typeof imageAsset === 'object' && 'height' in imageAsset && 'width' in imageAsset && 'uri' in imageAsset;
  }
  return false;
}
export function getNameFromPath(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

export function getExtensionFromPath(path: string): string {
  return path.slice(path.lastIndexOf('.') + 1);
}

export function getMimeFromExtension(extension: string): string {
  const mimes: Record<string, string> = {
    gif: 'image/gif',
    heic: 'image/heic',
    heif: 'image/heif',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
  };

  const mime = mimes[extension.toLowerCase()];

  if (!mime) {
    throw new Error(`Unsupported extension "${extension}", expected one of ${Object.keys(mimes).join(', ')}`);
  }

  return mime;
}

// Function to convert data URI to Blob
export const dataURItoBlob = (dataURI: string) => {
  const separatorIndex = dataURI.indexOf(',');
  const metadata = separatorIndex >= 0 ? dataURI.slice(0, separatorIndex) : '';
  const encodedData = separatorIndex >= 0 ? dataURI.slice(separatorIndex + 1) : '';
  const mimeString = /^data:([^;]+);/.exec(metadata)?.[1];
  if (!mimeString || !encodedData) throw new Error('Invalid data URI');

  const byteString = atob(encodedData);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
};

export function asFile(info: ImagePickerSuccessResult | ImagePickerAsset): File {
  const imageAsset = 'assets' in info && info.assets.length > 0 ? info.assets[0]! : (info as ImagePickerAsset);
  const uri = imageAsset.uri;
  const name = imageAsset.file?.name ?? imageAsset.fileName ?? getNameFromPath(uri);
  const type = imageAsset.file?.type ?? imageAsset.mimeType ?? pipe(uri, getExtensionFromPath, getMimeFromExtension);
  return {
    name,
    type,
    uri,
  };
}
